/**
 * PersonalDashboard (iter 73) — Editorial personal-finance command center.
 *
 * Design brief lives in /app/design_guidelines.json (design_agent output).
 * Warm, conversational, calm — not the accountant-grade look of the
 * Business dashboard. Renders for entities where `workspace_type === "personal"`.
 *
 * Sections (all data-testid'd):
 *   1. dashboard-topbar (greeting + rail-switcher + Add transaction)
 *   2. net-worth-hero-card
 *   3. ai-insights-card
 *   4. dashboard-wallets-strip
 *   5. monthly-cashflow-snapshot
 *   6. upcoming-bills-card
 *   7. dashboard-budgets-section
 *   8. dashboard-goals-section
 *   9. dashboard-investments-micro
 *  10. recent-transactions (with inline re-categorization)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import SavedDrillsCard from "@/components/SavedDrillsCard";
import { useAuth } from "@/context/AuthContext";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { formatCurrency, formatDate, relativeTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MultiTransactionForm from "@/components/transactions/MultiTransactionForm";
import { toast } from "sonner";
import {
  Sparkles, Plus, TrendingUp, TrendingDown, Wallet, PiggyBank, Target,
  BarChart3, Calendar, AlertTriangle, Loader2, RefreshCcw,
} from "lucide-react";

// -------- Helpers --------
function greetingTime() {
  const h = new Date().getHours();
  if (h < 5) return "Still up";
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

const RAIL_TINT = {
  USD: { border: "#1E5A96", bg: "#EFF6FC" },
  NGN: { border: "#2F7A55", bg: "#EEF7F2" },
};

// iter 73b — Personal workspace anchor is leaf green (per user preference).
// Keep it local to this file so Business dashboards still use slate teal.
const LEAF = "#3A8760";
const LEAF_DEEP = "#2F6B4A";

// -------- Rail switcher pill --------
function RailSwitcher() {
  const { activeRail, setActiveRail } = useCurrencyRail();
  const pills = [
    { id: "ALL", label: "All rails", testid: "rail-switch-all", bg: LEAF },
    { id: "USD", label: "USD", testid: "rail-switch-usd", bg: "#1E5A96" },
    { id: "NGN", label: "NGN", testid: "rail-switch-ngn", bg: "#2F7A55" },
  ];
  return (
    <div className="inline-flex p-1 rounded-full border border-[var(--divider)] bg-[var(--surface-elevated,var(--surface))]">
      {pills.map((p) => {
        const on = activeRail === p.id;
        return (
          <button
            key={p.id}
            data-testid={p.testid}
            onClick={() => setActiveRail(p.id)}
            className="px-3.5 h-8 rounded-full text-xs font-medium transition-all"
            style={on
              ? { background: p.bg, color: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }
              : { color: "var(--muted-text)" }}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

// -------- Section: Net Worth Hero --------
function NetWorthHero({ summary, accounts, portfolio }) {
  const { activeRail, isActive } = useCurrencyRail();

  // iter 72s — Net Worth is derived from ACTUAL account balances (banks +
  // cash + wallets + cards) plus investment market value. Rails are STRICTLY
  // separated — we never convert, never sum across currencies. Card accounts
  // carry their signed balance (negative = debt owed), so summing all
  // accounts naturally subtracts credit-card liabilities. We previously
  // re-derived cash from transaction flows (income − expense − saving),
  // which ignored opening balances and wrongly treated savings as leaving
  // net worth. See user-reported bug: "Net Worth $-436.14 includes $1,074.48
  // in investments" — the cash side had been mis-derived.
  const rails = useMemo(() => {
    const byCcy = {};
    // Sum every account's signed balance per currency. `card` accounts with
    // negative balances reduce net worth (they are money owed).
    for (const a of accounts || []) {
      const c = a.currency || "USD";
      if (!byCcy[c]) byCcy[c] = { currency: c, cash: 0, investments: 0, card_debt: 0 };
      byCcy[c].cash += Number(a.balance || 0);
      if (a.type === "card" && Number(a.balance || 0) < 0) {
        byCcy[c].card_debt += -Number(a.balance || 0); // positive display value
      }
    }
    for (const pRail of portfolio?.by_currency || []) {
      const c = pRail.currency;
      const v = Number(pRail.value || 0);
      if (!byCcy[c]) byCcy[c] = { currency: c, cash: 0, investments: 0, card_debt: 0 };
      byCcy[c].investments += v;
    }
    const out = Object.values(byCcy).map((r) => ({ ...r, balance: r.cash + r.investments }));
    out.sort((a, b) => (a.currency === "USD" ? -1 : b.currency === "USD" ? 1 : a.currency.localeCompare(b.currency)));
    return out;
  }, [accounts, portfolio]);

  // iter 72g — filter by isActive() string predicate (filterRails takes an
  // ARRAY; calling it with a string mistakenly passed every rail).
  const shown = rails.filter((r) => isActive(r.currency));

  // iter 72h — user preference: never convert across currencies. Rail=USD
  // shows only USD; rail=NGN shows only NGN; rail=ALL stacks each rail in
  // its own currency. The old "Convert to USD" toggle was removed.
  const singleRail = shown.length === 1 || activeRail !== "ALL";
  const singleValue = singleRail ? Number(shown[0]?.balance || 0) : null;
  const singleCcy = singleRail ? (activeRail === "ALL" ? shown[0]?.currency || "USD" : activeRail) : null;

  const wallets = (accounts || []).filter((a) => (activeRail === "ALL" || a.currency === activeRail));
  // iter 72s — runway uses liquid non-card cash only (cards are debt, not
  // spendable runway).
  const liquid = wallets.reduce((s, a) => (a.type === "card" ? s : s + Number(a.balance || 0)), 0);
  const runwayMonths = summary?.avg_monthly_expense
    ? (liquid / Number(summary.avg_monthly_expense)).toFixed(1)
    : null;

  return (
    <section
      data-testid="net-worth-hero-card"
      className="rounded-2xl p-8 sm:p-10 border border-[var(--divider)] bg-[var(--card)] relative overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-1" style={{ background: LEAF }} />
      <div className="text-[11px] tracking-[0.2em] uppercase text-[var(--muted-text)] font-semibold">
        Net Worth · {activeRail === "ALL" ? "all rails" : activeRail}
      </div>
      {singleRail ? (
        <div className="mt-2">
          <div data-testid="net-worth-amount" className="font-mono-data text-4xl sm:text-5xl font-semibold tracking-tight text-[var(--text)]">
            {formatCurrency(singleValue, singleCcy)}
          </div>
          {shown[0]?.investments > 0 && (
            <div className="text-xs text-[var(--muted-text)] mt-1">
              includes {formatCurrency(shown[0].investments, singleCcy)} in investments
            </div>
          )}
          {shown[0]?.card_debt > 0 && (
            <div data-testid="net-worth-card-debt" className="text-xs text-[var(--muted-text)] mt-1">
              net of {formatCurrency(shown[0].card_debt, singleCcy)} credit-card debt
            </div>
          )}
        </div>
      ) : (
        <div className="mt-2 flex flex-col gap-1">
          {shown.map((r) => (
            <div key={r.currency} className="flex items-baseline gap-3 flex-wrap">
              <span className="text-[10px] tracking-[0.2em] uppercase font-semibold px-1.5 py-0.5 rounded"
                    style={{ background: RAIL_TINT[r.currency]?.bg || "var(--hover)",
                              color: RAIL_TINT[r.currency]?.border || "var(--muted-text)" }}
                    data-testid={`net-worth-rail-label-${r.currency}`}>
                {r.currency}
              </span>
              <span data-testid={`net-worth-rail-${r.currency}`}
                    className="font-mono-data text-3xl sm:text-4xl font-semibold tracking-tight text-[var(--text)]">
                {formatCurrency(r.balance, r.currency)}
              </span>
              {r.investments > 0 && (
                <span className="text-[11px] text-[var(--muted-text)]">
                  incl {formatCurrency(r.investments, r.currency)} invested
                </span>
              )}
              {r.card_debt > 0 && (
                <span data-testid={`net-worth-rail-card-debt-${r.currency}`}
                      className="text-[11px] text-[var(--muted-text)]">
                  net of {formatCurrency(r.card_debt, r.currency)} card debt
                </span>
              )}
            </div>
          ))}
          <div className="text-xs text-[var(--muted-text)] mt-1">Each rail stays in its own currency — USD balances stay USD, NGN stays NGN.</div>
        </div>
      )}
      <p className="mt-4 text-sm text-[var(--muted-text)] max-w-2xl leading-relaxed">
        {runwayMonths
          ? <>Your liquid cash covers <b className="text-[var(--text)]">~{runwayMonths} months</b> of typical spending. You're financially healthy.</>
          : <>Add a few transactions and you'll see how long your cash runway lasts here.</>}
      </p>
    </section>
  );
}

// -------- Section: AI Insights --------
function AIInsightsCard() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const generate = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.post("/insights/generate", {});
      setText(data?.summary || data?.insight || "No insight available yet.");
    } catch (e) {
      setErr(e.response?.status === 503 ? "AI temporarily unavailable — check your key balance." : "Couldn't generate insight");
    } finally {
      setLoading(false);
    }
  };
  return (
    <section data-testid="ai-insights-card"
             className="rounded-2xl p-6 border border-[#DAE9EA] bg-[#E9F5F6]/40 relative">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#156B73]/10 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-[#156B73]" />
        </div>
        <div className="text-[11px] tracking-[0.2em] uppercase text-[#156B73] font-semibold">Your AI companion</div>
      </div>
      {text ? (
        <p data-testid="ai-insights-text" className="text-sm leading-relaxed text-[var(--text)]">{text}</p>
      ) : (
        <p className="text-sm text-[var(--muted-text)] italic">Tap below and I'll spot a pattern in this month's money.</p>
      )}
      {err && <p className="text-xs text-[color:var(--danger,#D94E34)] mt-2">{err}</p>}
      <Button
        data-testid="generate-insights-btn"
        variant="ghost"
        size="sm"
        onClick={generate}
        disabled={loading}
        className="mt-3 text-[#156B73] hover:bg-[#156B73]/10"
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCcw className="w-3.5 h-3.5 mr-1.5" />}
        {text ? "Get another insight" : "Generate insight"}
      </Button>
    </section>
  );
}

// -------- Section: Wallets Strip --------
function WalletsStrip({ accounts }) {
  const { isActive } = useCurrencyRail();
  // iter 72g — use isActive(ccy) predicate; filterRails takes an ARRAY.
  const shown = (accounts || []).filter((a) => isActive(a.currency));
  if (!shown.length) {
    return (
      <section data-testid="dashboard-wallets-strip" className="rounded-2xl p-6 border border-dashed border-[var(--divider)] bg-[var(--card)] text-center">
        <Wallet className="w-6 h-6 mx-auto mb-2 text-[var(--muted-text)]" />
        <p className="text-sm text-[var(--muted-text)]">No wallets on this rail yet — head to Accounts to add one.</p>
      </section>
    );
  }
  return (
    <section data-testid="dashboard-wallets-strip" className="space-y-3">
      <div className="text-[11px] tracking-[0.2em] uppercase text-[var(--muted-text)] font-semibold">Your money</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {shown.map((a) => {
          const tint = RAIL_TINT[a.currency] || { border: "#2F4F4F", bg: "transparent" };
          return (
            <div key={a.id}
                 data-testid={`wallet-card-${a.id}`}
                 className="p-4 rounded-xl bg-[var(--card)] border border-[var(--divider)] hover:shadow-sm transition-shadow"
                 style={{ borderLeftWidth: 4, borderLeftColor: tint.border }}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[var(--text)] truncate">{a.name}</div>
                  <div className="text-[11px] uppercase tracking-wider text-[var(--muted-text)] mt-0.5">
                    {a.type || "Account"} · {a.currency}
                  </div>
                </div>
              </div>
              <div className="mt-3 font-mono-data text-xl font-semibold text-[var(--text)]">
                {formatCurrency(a.balance, a.currency)}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// -------- Section: Cash Flow Snapshot --------
function CashFlowSnapshot({ summary }) {
  const { activeRail, isActive } = useCurrencyRail();
  // iter 72g — filter by rail, and NEVER sum across currencies. When rail=ALL
  // and 2+ rails exist, render a stacked per-rail breakdown so USD income
  // and NGN income aren't merged into a bogus mixed number.
  const rails = (summary?.by_currency || []).filter((r) => isActive(r.currency));
  const multi = activeRail === "ALL" && rails.length > 1;

  if (multi) {
    return (
      <section data-testid="monthly-cashflow-snapshot"
               className="rounded-2xl p-6 border border-[var(--divider)] bg-[var(--card)]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] tracking-[0.2em] uppercase text-[var(--muted-text)] font-semibold">This month · by rail</div>
            <h2 data-testid="cashflow-balance-headline" className="text-lg font-medium mt-1 text-[var(--text)]">
              Each currency tracked separately.
            </h2>
          </div>
          <BarChart3 className="w-5 h-5 text-[var(--muted-text)]" />
        </div>
        <div className="space-y-4">
          {rails.map((r) => {
            const inc = Number(r.income || 0), exp = Number(r.expense || 0);
            const p = inc > 0 ? Math.min(100, Math.round((exp / inc) * 100)) : 0;
            const net = inc - exp;
            return (
              <div key={r.currency} data-testid={`cashflow-rail-${r.currency}`}>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="font-semibold text-[var(--muted-text)] tracking-widest uppercase">{r.currency}</span>
                  <span className="text-[var(--muted-text)]">Net {formatCurrency(net, r.currency)}</span>
                </div>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-[#2F6B4A] font-medium">Earned {formatCurrency(inc, r.currency)}</span>
                  <span className="text-[#D94E34] font-medium">Spent {formatCurrency(exp, r.currency)}</span>
                </div>
                <div className="h-2.5 rounded-full bg-[#F0ECE1] overflow-hidden flex">
                  <div className="bg-[#2F6B4A]" style={{ width: `${inc > 0 ? 100 - p : 0}%` }} />
                  <div className="bg-[#D94E34]" style={{ width: `${p}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  }

  const single = rails[0] || { income: 0, expense: 0, currency: "USD" };
  const income = Number(single.income || 0);
  const expense = Number(single.expense || 0);
  const net = income - expense;
  const pct = income > 0 ? Math.min(100, Math.round((expense / income) * 100)) : 0;
  const ccy = single.currency || (activeRail === "ALL" ? "USD" : activeRail);
  const remaining = Math.max(0, income - expense);
  const headline = income === 0
    ? "No income this month yet — add a paycheque or transaction."
    : net > 0
      ? `You've got ${formatCurrency(remaining, ccy)} left this month.`
      : `You've spent ${pct}% of what came in — take a breath.`;
  return (
    <section data-testid="monthly-cashflow-snapshot"
             className="rounded-2xl p-6 border border-[var(--divider)] bg-[var(--card)]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-[var(--muted-text)] font-semibold">This month</div>
          <h2 data-testid="cashflow-balance-headline" className="text-lg font-medium mt-1 text-[var(--text)]">{headline}</h2>
        </div>
        <BarChart3 className="w-5 h-5 text-[var(--muted-text)]" />
      </div>
      <div className="space-y-4">
        <div>
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-[#2F6B4A] font-medium">Earned {formatCurrency(income, ccy)}</span>
            <span className="text-[#D94E34] font-medium">Spent {formatCurrency(expense, ccy)}</span>
          </div>
          <div className="h-2.5 rounded-full bg-[#F0ECE1] overflow-hidden flex">
            <div className="bg-[#2F6B4A]" style={{ width: `${income > 0 ? 100 - pct : 0}%` }} />
            <div className="bg-[#D94E34]" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 pt-3 border-t border-[var(--divider)]">
          <MiniStat label="Net this month" value={formatCurrency(net, ccy)} tone={net >= 0 ? "pos" : "neg"} />
          <MiniStat label="Days left" value={String(daysLeftInMonth())} />
          <MiniStat label="% used" value={`${pct}%`} />
        </div>
      </div>
    </section>
  );
}

function MiniStat({ label, value, tone }) {
  const color = tone === "pos" ? "#2F6B4A" : tone === "neg" ? "#D94E34" : "var(--text)";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)]">{label}</div>
      <div className="font-mono-data text-sm font-semibold mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

function daysLeftInMonth() {
  const d = new Date();
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return Math.max(0, last.getDate() - d.getDate());
}

// -------- Section: Upcoming Bills --------
function UpcomingBillsCard({ recurring }) {
  const soon = (recurring || [])
    .filter((r) => r.type === "expense" && r.next_run)
    .sort((a, b) => (a.next_run || "").localeCompare(b.next_run || ""))
    .slice(0, 5);
  return (
    <section data-testid="upcoming-bills-card"
             className="rounded-2xl p-6 border border-[var(--divider)] bg-[var(--card)]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-[var(--muted-text)] font-semibold">Coming up</div>
          <h2 className="text-lg font-medium mt-1 text-[var(--text)]">Next 14 days</h2>
        </div>
        <Calendar className="w-5 h-5 text-[var(--muted-text)]" />
      </div>
      {soon.length === 0 ? (
        <p className="text-sm text-[var(--muted-text)] italic">No scheduled bills — add a recurring expense in Rules.</p>
      ) : (
        <ul className="space-y-2.5">
          {soon.map((b) => {
            const days = daysUntil(b.next_run);
            return (
              <li key={b.id} data-testid={`upcoming-bill-${b.id}`}
                  className="flex items-center justify-between p-2.5 rounded-lg hover:bg-[var(--surface)] transition-colors">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[var(--text)] truncate">{b.description || b.category}</div>
                  <div className="text-xs text-[var(--muted-text)]">{b.category} · in {days} day{days === 1 ? "" : "s"}</div>
                </div>
                <div className="font-mono-data text-sm text-[#D94E34] font-semibold shrink-0 ml-3">
                  −{formatCurrency(b.amount, b.currency || "USD")}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function daysUntil(iso) {
  try {
    const d = new Date(iso).setHours(0, 0, 0, 0);
    const today = new Date().setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((d - today) / 86400000));
  } catch { return 0; }
}

// -------- Section: Budgets Progress --------
function BudgetsSection({ budgets }) {
  const top = (budgets || []).slice(0, 5);
  return (
    <section data-testid="dashboard-budgets-section"
             className="rounded-2xl p-6 border border-[var(--divider)] bg-[var(--card)]">
      <div className="text-[11px] tracking-[0.2em] uppercase text-[var(--muted-text)] font-semibold">Budget guardrails</div>
      <h2 className="text-lg font-medium mt-1 mb-4 text-[var(--text)]">Where you stand</h2>
      {top.length === 0 ? (
        <p className="text-sm text-[var(--muted-text)] italic">Set your first budget in the Budgets tab.</p>
      ) : (
        <div className="space-y-4">
          {top.map((b) => {
            const spent = Number(b.spent || 0);
            const limit = Number(b.limit || 1);
            const pct = Math.min(100, Math.round((spent / limit) * 100));
            const status = pct >= 100 ? "over" : pct >= 80 ? "warn" : "ok";
            const color = status === "over" ? "#D94E34" : status === "warn" ? "#D97746" : "#2F6B4A";
            const remaining = Math.max(0, limit - spent);
            return (
              <div key={b.id || b.category} data-testid={`dashboard-budget-${b.category}`}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="font-medium text-[var(--text)]">{b.category}</span>
                  <span className="text-xs text-[var(--muted-text)]">
                    {formatCurrency(remaining, b.currency || "USD")} remaining of {formatCurrency(limit, b.currency || "USD")}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-[#F0ECE1] overflow-hidden">
                  <div style={{ width: `${pct}%`, background: color }} className="h-full transition-all" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// -------- Section: Savings Goals --------
function GoalsSection({ goals }) {
  const top = (goals || []).slice(0, 4);
  return (
    <section data-testid="dashboard-goals-section"
             className="rounded-2xl p-6 border border-[var(--divider)] bg-[var(--card)]">
      <div className="text-[11px] tracking-[0.2em] uppercase text-[var(--muted-text)] font-semibold">Savings ambitions</div>
      <h2 className="text-lg font-medium mt-1 mb-4 text-[var(--text)]">What you're chasing</h2>
      {top.length === 0 ? (
        <div className="text-center py-4">
          <Target className="w-6 h-6 mx-auto mb-2 text-[var(--muted-text)]" />
          <p className="text-sm text-[var(--muted-text)]">No goals yet — pick one to start.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {top.map((g) => {
            const cur = Number(g.current_amount || 0);
            const target = Number(g.target_amount || 1);
            const pct = Math.min(100, Math.round((cur / target) * 100));
            return (
              <div key={g.id} data-testid={`dashboard-goal-${g.id}`}
                   className="p-3 rounded-lg hover:bg-[var(--surface)] transition-colors">
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="font-medium text-[var(--text)]">{g.name}</span>
                  <span className="text-xs text-[var(--muted-text)]">{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-[#F0ECE1] overflow-hidden mb-1">
                  <div style={{ width: `${pct}%`, background: "#D97746" }} className="h-full transition-all" />
                </div>
                <div className="flex justify-between text-[11px] text-[var(--muted-text)]">
                  <span>{formatCurrency(cur, g.currency || "USD")}</span>
                  <span>of {formatCurrency(target, g.currency || "USD")}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// -------- Section: Investments Micro --------
function InvestmentsMicro({ portfolio }) {
  const value = Number(portfolio?.market_value || 0);
  const cost = Number(portfolio?.cost_basis || 0);
  const gain = value - cost;
  const gainPct = cost > 0 ? (gain / cost) * 100 : 0;
  const up = gain >= 0;
  return (
    <section data-testid="dashboard-investments-micro"
             className="rounded-2xl p-6 border border-[var(--divider)] bg-[var(--card)]">
      <div className="flex items-center gap-2 mb-3">
        <PiggyBank className="w-4 h-4 text-[#B47E1E]" />
        <div className="text-[11px] tracking-[0.2em] uppercase text-[var(--muted-text)] font-semibold">Investments</div>
      </div>
      <div className="font-mono-data text-2xl font-semibold text-[var(--text)]">
        {formatCurrency(value, "USD")}
      </div>
      {cost > 0 ? (
        <div className="mt-2 flex items-center gap-1.5 text-sm">
          {up ? <TrendingUp className="w-4 h-4 text-[#2F6B4A]" /> : <TrendingDown className="w-4 h-4 text-[#D94E34]" />}
          <span style={{ color: up ? "#2F6B4A" : "#D94E34" }} className="font-medium">
            {up ? "+" : ""}{formatCurrency(gain, "USD")} ({up ? "+" : ""}{gainPct.toFixed(1)}%)
          </span>
        </div>
      ) : (
        <p className="text-xs text-[var(--muted-text)] italic mt-2">No holdings yet.</p>
      )}
    </section>
  );
}

// -------- Section: Recent Transactions with inline recat --------
function RecentTransactions({ txns, categories, onReload }) {
  const [saving, setSaving] = useState(null);
  const patch = async (tx, newCat) => {
    if (newCat === tx.category) return;
    setSaving(tx.id);
    try {
      await api.patch(`/transactions/${tx.id}`, { category: newCat });
      toast.success("Category updated");
      onReload?.();
    } catch (e) {
      toast.error("Couldn't update category");
    } finally {
      setSaving(null);
    }
  };
  return (
    <section data-testid="recent-transactions"
             className="rounded-2xl p-6 border border-[var(--divider)] bg-[var(--card)]">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] tracking-[0.2em] uppercase text-[var(--muted-text)] font-semibold">Recent activity</div>
          <h2 className="text-lg font-medium mt-1 text-[var(--text)]">Last few moves</h2>
        </div>
      </div>
      {txns.length === 0 ? (
        <p className="text-sm text-[var(--muted-text)] italic">No transactions yet.</p>
      ) : (
        <ul className="divide-y divide-[var(--divider)]">
          {txns.map((t) => {
            const isExp = t.type === "expense";
            const catList = categories.filter((c) => c.type === t.type);
            return (
              <li key={t.id} data-testid={`recent-tx-${t.id}`}
                  className="py-3 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isExp ? "bg-[#FDF1EE]" : "bg-[#EBF6F0]"}`}>
                  {isExp ? <TrendingDown className="w-4 h-4 text-[#D94E34]" /> : <TrendingUp className="w-4 h-4 text-[#2F6B4A]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[var(--text)] truncate">{t.note || t.category}</div>
                  <div className="text-xs text-[var(--muted-text)] flex items-center gap-2 mt-0.5">
                    <span>{relativeTime(t.created_at || t.date)}</span>
                    <Select
                      value={t.category}
                      onValueChange={(v) => patch(t, v)}
                      disabled={saving === t.id}
                    >
                      <SelectTrigger data-testid={`tx-category-select-${t.id}`}
                                     className="h-6 text-[11px] px-2 border-[var(--divider)] bg-transparent w-auto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {catList.length === 0 && <SelectItem value={t.category}>{t.category}</SelectItem>}
                        {catList.map((c) => (
                          <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className={`font-mono-data text-sm font-semibold shrink-0 ${isExp ? "text-[#D94E34]" : "text-[#2F6B4A]"}`}>
                  {isExp ? "−" : "+"}{formatCurrency(t.amount, t.currency || "USD")}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

// -------- Main --------
export default function PersonalDashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [goals, setGoals] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [recurring, setRecurring] = useState([]);
  const [txns, setTxns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    // Parallel fetch — offline safety via per-call catch so any one failure
    // doesn't blank the whole dashboard. Some endpoints return {rows:[]} or
    // {items:[]} envelopes, so we normalize to plain arrays right here.
    const arr = (d) => Array.isArray(d) ? d : Array.isArray(d?.rows) ? d.rows
                       : Array.isArray(d?.items) ? d.items
                       : Array.isArray(d?.progress) ? d.progress
                       : Array.isArray(d?.transactions) ? d.transactions : [];
    const safeArr = (p) => p.then((r) => arr(r.data)).catch(() => []);
    const safeObj = (p) => p.then((r) => r.data).catch(() => null);
    const [s, a, b, g, port, rec, tx, cats] = await Promise.all([
      safeObj(api.get("/analytics/summary")),
      safeArr(api.get("/accounts")),
      safeArr(api.get("/budgets/progress")),
      safeArr(api.get("/goals")),
      safeObj(api.get("/investments/portfolio")),
      safeArr(api.get("/recurring")),
      safeArr(api.get("/transactions?limit=8")),
      safeArr(api.get("/categories")),
    ]);
    setSummary(s); setAccounts(a); setBudgets(b); setGoals(g);
    setPortfolio(port); setRecurring(rec); setTxns(tx); setCategories(cats);
  }, []);

  useEffect(() => { load(); }, [load]);

  const firstName = useMemo(() => (user?.name || "").split(" ")[0] || "there", [user]);

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Topbar */}
        <header data-testid="dashboard-topbar"
                className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-[var(--divider)]">
          <div>
            <span className="text-[11px] tracking-[0.2em] uppercase text-[var(--muted-text)] font-semibold">
              Personal sanctuary
            </span>
            <h1 data-testid="dashboard-greeting"
                className="text-3xl sm:text-4xl font-semibold tracking-tight mt-1 text-[var(--text)]">
              {greetingTime()}, {firstName}
            </h1>
            <p className="text-xs text-[var(--muted-text)] mt-1">
              {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
              {" · All accounts synced"}
            </p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <RailSwitcher />
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-transaction-btn"
                        style={{ backgroundColor: LEAF }}
                        className="text-white hover:opacity-90 transition-opacity">
                  <Plus className="w-4 h-4 mr-1.5" /> Log something
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="add-transaction-dialog" className="w-[96vw] max-w-[1240px] sm:max-w-[1240px]">
                <DialogHeader>
                  <DialogTitle>New transactions</DialogTitle>
<DialogDescription className="text-xs">Enter one or more lines, then save them together.</DialogDescription>
                </DialogHeader>
                <MultiTransactionForm
                  currency={user?.currency || "USD"}
                  onDone={(n) => { setAddOpen(false); toast.success(`${n} transaction${n === 1 ? "" : "s"} added`); load(); }}
                  onCancel={() => setAddOpen(false)}
                />
              </DialogContent>
            </Dialog>
          </div>
        </header>

        {/* Hero row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-8">
            <NetWorthHero summary={summary} accounts={accounts} portfolio={portfolio} />
          </div>
          <div className="lg:col-span-4">
            <AIInsightsCard />
          </div>
        </div>

        {/* Wallets strip */}
        <WalletsStrip accounts={accounts} />

        {/* Cash flow + upcoming bills */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7">
            <CashFlowSnapshot summary={summary} />
          </div>
          <div className="lg:col-span-5">
            <UpcomingBillsCard recurring={recurring} />
          </div>
        </div>

        {/* Budgets + Goals + Investments */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-5">
            <BudgetsSection budgets={budgets} />
          </div>
          <div className="lg:col-span-4">
            <GoalsSection goals={goals} />
          </div>
          <div className="lg:col-span-3">
            <InvestmentsMicro portfolio={portfolio} />
          </div>
        </div>

        {/* Recent transactions */}
        <RecentTransactions txns={txns} categories={categories} onReload={load} />

        {/* iter 72p — Saved drill shortcuts */}
        <SavedDrillsCard />

        {/* Ending flourish */}
        <div className="text-center pt-4 pb-2">
          <p className="text-[11px] tracking-[0.15em] uppercase text-[var(--muted-text)]">
            Personal workspace · Nothing here mixes with your Business books
          </p>
        </div>
      </div>
    </AppShell>
  );
}
